import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faXmark, faArrowLeft, faTrash, faSpinner } from '@fortawesome/free-solid-svg-icons';
import {
    Dialog,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import { toast } from 'react-toastify';
import LoadingOverlay from '../../../components/loadingoverlay/LoadingOverlay';
import { useFeatures } from "../../../utils/featureContext";
import { FEATURES } from '../../../utils/extractFeaturesList';
import { getDependencies, getDepFilters, CreateDependency, deleteDependency } from '../../../services/scm/Dependencies';
import { getAssetTypes, getAllThings } from '../../../services/scm/Utils';
import "./ManageDependencies.css";
import CustomInput from '../../../components/textField/scmInput/CustomInput';

const Required = () => <span className="text-danger ms-1">*</span>;
const isNonEmpty = (v) => String(v ?? '').trim().length > 0;
const MAX_DEPENDENCIES = 5;

// Common menu props for all Selects to prevent overlap / huge width
const selectMenuProps = {
    MenuProps: {
        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
        transformOrigin: { vertical: 'top', horizontal: 'left' },
        PaperProps: {
            style: {
                maxHeight: 240,
                maxWidth: 320,
            },
        },
    },
};

export default function ManageDependencies() {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const navState = (location && location.state) || {};

    const pack = navState.packageName;
    const ver = navState.versionName;

    const [allGroups, setAllGroups] = useState([]);   // for thing_group_names + table
    const [assetTypes, setAssetTypes] = useState([]); // for thing_type_name
    const [things, setThings] = useState([]);         // for include_things / exclude_things
    const [loading, setLoading] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [deletingGroupArn, setDeletingGroupArn] = useState(null);
    const [deleteErrorOpen, setDeleteErrorOpen] = useState(false);
    const [deleteErrorInfo, setDeleteErrorInfo] = useState({
        message: '',
        dependencies: [],
        groupName: '',
    });
    const [depOperator, setDepOperator] = useState('==');
    const [operatorByKey, setOperatorByKey] = useState({});

    // Dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogStep, setDialogStep] = useState(1);  // 1 = build, 2 = summary
    const [newGroupName, setNewGroupName] = useState('');
    const [allFilters, setAllFilters] = useState([]); // ["location","software_version","thing_group_names",...]

    // New dependencies builder state
    const [depKey, setDepKey] = useState('');
    const [depValue, setDepValue] = useState('');
    const [depEntries, setDepEntries] = useState([]);

    const isEditable = true;

    const { features, loading: featuresLoading } = useFeatures();

    const hasAccess = (featureName) => {
        if (featuresLoading) return false;
        return features?.includes(featureName);
    };

    const isScmAdmin = hasAccess(FEATURES.SCM_ADMIN);

    const handleCloseDeleteError = () => {
        setDeleteErrorOpen(false);
    };

    // Build filters object { key: [values...] } from depEntries
    const filtersJson = useMemo(() => {
        const obj = {};
        const exclude = [];
        depEntries.forEach(({ key, value, operator }) => {
            if (!obj[key]) obj[key] = [];
            obj[key].push(value);
            if (operator === '!=') {
                if (!exclude.includes(key)) exclude.push(key);
            }
        });
        if (exclude.length > 0) obj.exclude_filters = exclude;
        return obj;
    }, [depEntries]);

    const filtersWithOps = useMemo(() => {
        const map = {};
        depEntries.forEach(({ key, value, operator }) => {
            if (!map[key]) map[key] = { operator, values: [] };
            map[key].values.push(value);
        });
        return map;
    }, [depEntries]);

    const formatFilters = (filters) => {
        if (!filters || typeof filters !== 'object') return '';

        const excludeKeys = Array.isArray(filters.exclude_filters) ? filters.exclude_filters : [];

        return Object.keys(filters)
            .filter((key) => key !== 'exclude_filters')
            .map((key) => {
                const value = filters[key];
                const operator = excludeKeys.includes(key) ? '!=' : '==';
                const formattedValue = Array.isArray(value) ? value.join(', ') : String(value);
                return `${key} ${operator} ${formattedValue}`;
            })
            .join(' & ');
    };

    const formatForConditionPreview = (filtersMap) => {
        const keys = Object.keys(filtersMap);
        if (!keys.length) return '{}';

        return (
            '{\n' +
            keys
                .map((key, index) => {
                    const { operator, values } = filtersMap[key];
                    const condition = values
                        .map(v => `"${v}"`)
                        .join(' OR ');

                    const ending = index === keys.length - 1 ? '' : ', AND';
                    return `  ${key} ${operator} [${condition}]${ending}`;
                })
                .join('\n') +
            '\n}'
        );
    };

    const getAllDependencies = async () => {
        try {
            setLoading(true);
            const raw = await getDependencies();
            const arr = Array.isArray(raw) ? raw : raw?.data ?? [];
            const normalized = (arr || [])
                .map(x => ({
                    thingGroupName: x.thingGroupName ?? x.name,
                    thingGroupArn: x.thingGroupArn ?? x.arn ?? x.id,
                    filters: x.filters ?? {},
                }))
                .filter(x => x.thingGroupName && x.thingGroupArn);

            setAllGroups(normalized);
            setLoading(false);
        } catch (err) {
            console.error('getAllDependencies error', err);
            toast.error(t('Failed to load dependencies.'));
            setLoading(false);
        }
    };

    const getAllFilters = async () => {
        try {
            const raw = await getDepFilters();
            // { query_fields: [...] }
            setAllFilters(raw?.query_fields || []);
        } catch (err) {
            console.error('getDepFilters error', err);
            setAllFilters([]);
        }
    };

    const loadAssetTypes = async () => {
        try {
            const raw = await getAssetTypes();
            setAssetTypes(raw?.thingTypes || []);
        } catch (err) {
            console.error('getAssetTypes error', err);
            setAssetTypes([]);
        }
    };

    const loadThings = async () => {
        try {
            const raw = await getAllThings();
            // { things: [{ thingName, thingArn, thingTypeName }, ...] }
            setThings(raw?.things || []);
        } catch (err) {
            console.error('getAllThings error', err);
            setThings([]);
        }
    };

    const removeOne = async (groupArn) => {
        if (!isEditable) return;

        const group = allGroups.find(g => g.thingGroupArn === groupArn);
        if (!group) return;

        setDeletingGroupArn(groupArn);
        try {
            await deleteDependency({ thingGroupName: group.thingGroupName });
            setAllGroups(prev => prev.filter(g => g.thingGroupArn !== groupArn));
            toast.success(t('Dependency group removed.'));
        } catch (err) {
            console.error('Delete dependency error:', err);

            const status = err?.status || err?.response?.status;
            const apiMsg =
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                err?.message;

            if (status === 409) {
                const dependencies = err?.response?.data?.dependencies || [];
                setDeleteErrorInfo({
                    message: apiMsg || t('Thing group is used in package versions'),
                    dependencies,
                    groupName: group.thingGroupName,
                });
                setDeleteErrorOpen(true);
            } else {
                toast.error(t('Failed to remove dependency group.'));
            }
        } finally {
            setDeletingGroupArn(null);
        }
    };
    useEffect(() => {
        if (!depKey) return;
        const nextOp = operatorByKey[depKey] || '==';
        setDepOperator(nextOp);
    }, [depKey, operatorByKey]);

    const handleOperatorChange = (newOperator) => {
        if (!depKey) return;

        setDepOperator(newOperator);
        setOperatorByKey((prev) => ({ ...prev, [depKey]: newOperator }));
        setDepEntries((prev) =>
            prev.map((entry) =>
                entry.key === depKey ? { ...entry, operator: newOperator } : entry
            )
        );
    };

    useEffect(() => {
        getAllDependencies();
        getAllFilters();
        loadAssetTypes();
        loadThings();
    }, []);

    // Dialog handlers
    const handleOpenDialog = () => {
        setNewGroupName('');
        setDepKey('');
        setDepValue('');
        setDepEntries([]);
        setDialogStep(1);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setDialogStep(1);
    };

    const handleAddDependency = () => {
        if (!depKey || !isNonEmpty(depValue)) return;
        const operator = operatorByKey[depKey] || depOperator;

        setOperatorByKey((prev) => ({ ...prev, [depKey]: operator }));
        setDepEntries((prev) => [
            ...prev,
            { key: depKey, value: depValue, operator },
        ]);
        setDepValue('');
    };

    const handleRemoveEntry = (indexToRemove) => {
        setDepEntries(prev => prev.filter((_, idx) => idx !== indexToRemove));
    };

    const handleNext = () => {
        if (!isNonEmpty(newGroupName)) {
            toast.warn(t('Dependency group name is required.'));
            return;
        }
        if (depEntries.length === 0) {
            toast.warn(t('Please add at least one dependency.'));
            return;
        }
        setDialogStep(2);
    };

    const handleCreate = async () => {
        if (!isNonEmpty(newGroupName)) {
            toast.warn(t('Dependency group name is required.'));
            return;
        }

        if (!Object.keys(filtersJson).length) {
            toast.warn(t('Please add at least one dependency.'));
            return;
        }

        const confirmed = window.confirm(
            t('Once saved, you cannot edit any of the fields. Do you want to proceed?')
        );

        if (!confirmed) {
            return;
        }

        try {
            const jsonPayload = {
                thingGroupName: newGroupName.trim(),
                filters: filtersJson
            }
            setCreateLoading(true);
            const res = await CreateDependency(jsonPayload);
            getAllDependencies();
            toast.success(t('Dependency group created successfully.'));
            setDialogOpen(false);
            setDialogStep(1);
        } catch (err) {
            console.error('Create dependency error:', err);

            const status = err?.status || err?.response?.status;
            const apiMsg =
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                err?.message;

            if (status === 409) {
                toast.error(apiMsg || t('A dependency group with this name already exists.'));
            } else {
                toast.error(t('Failed to create dependency group.'));
            }
        } finally {
            setCreateLoading(false);
        }
    };

    // Dynamic value field based on depKey
    const renderValueField = () => {
        // 1) location / software_version -> plain text field
        if (depKey === 'location' || depKey === 'software_version') {
            return (
                <TextField
                    size="small"
                    fullWidth
                    label={t('Value')}
                    value={depValue}
                    onChange={e => setDepValue(e.target.value)}
                />
            );
        }

        // 2) thing_group_names -> dropdown from allGroups
        if (depKey === 'thing_group_names') {
            return (
                <FormControl size="small" fullWidth>
                    <InputLabel id="dep-value-label">{t('Value')}</InputLabel>
                    <Select
                        labelId="dep-value-label"
                        label={t('Value')}
                        value={depValue}
                        onChange={e => setDepValue(e.target.value)}
                        {...selectMenuProps}
                    >
                        {allGroups.map(g => (
                            <MenuItem key={g.thingGroupArn} value={g.thingGroupName}>
                                {g.thingGroupName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );
        }

        // 3) thing_type_name -> dropdown from assetTypes
        if (depKey === 'thing_type_name') {
            return (
                <FormControl size="small" fullWidth>
                    <InputLabel id="dep-value-label">{t('Value')}</InputLabel>
                    <Select
                        labelId="dep-value-label"
                        label={t('Value')}
                        value={depValue}
                        onChange={e => setDepValue(e.target.value)}
                        {...selectMenuProps}
                    >
                        {assetTypes.map(type => (
                            <MenuItem key={type} value={type}>
                                {type}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );
        }

        // 4) include_things / exclude_things -> dropdown from things
        if (depKey === 'include_things' || depKey === 'exclude_things') {
            return (
                <FormControl size="small" fullWidth>
                    <InputLabel id="dep-value-label">{t('Value')}</InputLabel>
                    <Select
                        labelId="dep-value-label"
                        label={t('Value')}
                        value={depValue}
                        onChange={e => setDepValue(e.target.value)}
                        {...selectMenuProps}
                    >
                        {things.map(thing => (
                            <MenuItem key={thing.thingArn} value={thing.thingName}>
                                {thing.thingName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );
        }

        // 5) default fallback: TextField
        return (
            <TextField
                size="small"
                fullWidth
                label={t('Value')}
                value={depValue}
                onChange={e => setDepValue(e.target.value)}
            />
        );
    };

    const canAdd = depKey && isNonEmpty(depValue);
    const canGoNext = isNonEmpty(newGroupName) && depEntries.length > 0;

    return (
        <div className="container my-3">
            <div className="card-body col-12 col-xxl-10 responsive-col">
                {/* HEADER + CREATE BUTTON */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center gap-0">
                        {pack && ver && <button
                            className="btn btn-transperant"
                            onClick={() => navigate(-1)}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </button>}

                        <h5 className="card-title mb-0">{t('Manage Dependencies')}</h5>
                    </div>

                    <button
                        className="btn btn-primary d-flex align-items-center gap-2"
                        type="button"
                        onClick={handleOpenDialog}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        <span>{t('Create')}</span>
                    </button>
                </div>

                {/* MAIN TABLE CARD */}
                <div className="card mt-2 no-border">
                    <div className="card-body">
                        <h6 className="mb-3">{t('Dependency Groups')}</h6>

                        {allGroups.length === 0 ? (
                            <div className="text-muted">{t('No dependency groups found.')}</div>
                        ) : (
                            <div className="table-responsive ad-table-wrap manage-deps-table-wrapper">
                                <table className="table table-sm align-middle">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '25%' }}>{t('Dependency Group')}</th>
                                            <th style={{ width: '60%' }}>{t('Filters')}</th>
                                            {isScmAdmin && <th style={{ width: '15%' }}>{t('Actions')}</th>}
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {allGroups.map(g => (
                                            <tr key={g.thingGroupArn}>
                                                <td className="fw-semibold">{g.thingGroupName}</td>
                                                <td className="text-muted filters-cell">
                                                    {formatFilters(g.filters)}
                                                </td>
                                                {isScmAdmin && (
                                                    <td>
                                                        <button
                                                            className="btn btn-transperant"
                                                            onClick={() => removeOne(g.thingGroupArn)}
                                                            disabled={deletingGroupArn !== null}
                                                        >
                                                            {deletingGroupArn === g.thingGroupArn
                                                                ? <FontAwesomeIcon icon={faSpinner} spin />
                                                                : <FontAwesomeIcon icon={faTrash} style={{ color: 'red' }} />
                                                            }
                                                        </button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                {/* CREATE / SUMMARY MODAL */}
                <Dialog
                    open={dialogOpen}
                    onClose={handleCloseDialog}
                    fullWidth
                    maxWidth="sm"
                >
                    <div className="p-3">
                        {/* Title */}
                        <h5 className="mb-3">
                            {dialogStep === 1
                                ? t('New Dependency Group')
                                : t('Dependency Group Summary')}
                        </h5>

                        {dialogStep === 1 ? (
                            /* STEP 1: build filters */
                            <div className="d-flex flex-column gap-2">
                                {/* Group name */}
                                <CustomInput
                                    label={<>{t('Dependency Group Name')}<Required /></>}
                                    value={newGroupName}
                                    onChange={(e) => setNewGroupName(e.target.value)}
                                    placeholder={t('Enter dependency group name')}
                                    allowedChars={/^[A-Za-z0-9_-]*$/}
                                    disabled={false}
                                />
                                {!isNonEmpty(newGroupName) && (
                                    <small className="text-danger">
                                        {t('Dependency group name is required')}
                                    </small>
                                )}

                                {/* Row: Fields dropdown | Value | + button */}
                                <div className="d-flex align-items-center gap-2">
                                    <FormControl size="small" fullWidth>
                                        <InputLabel id="dep-key-label">{t('Fields')}</InputLabel>
                                        <Select
                                            labelId="dep-key-label"
                                            label={t('Fields')}
                                            value={depKey}
                                            disabled={depEntries.length >= MAX_DEPENDENCIES}
                                            onChange={e => {
                                                setDepKey(e.target.value);
                                                setDepValue('');
                                            }}
                                            {...selectMenuProps}
                                        >
                                            {allFilters.map(field => (
                                                <MenuItem key={field} value={field}>
                                                    {field}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {depKey !== 'exclude_things' && (
                                        <FormControl size="small" sx={{ minWidth: 100 }}>
                                            <InputLabel id="dep-op-label">{t('Operator')}</InputLabel>
                                            <Select
                                                labelId="dep-op-label"
                                                label={t('Operator')}
                                                value={depOperator}
                                                onChange={(e) => handleOperatorChange(e.target.value)}
                                                {...selectMenuProps}
                                            >
                                                <MenuItem value="==" disabled={depEntries.some(d => d.key === depKey && d.operator === '!=')}>
                                                    ==
                                                </MenuItem>
                                                <MenuItem value="!=" disabled={depEntries.some(d => d.key === depKey && d.operator === '==')}>
                                                    !=
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    )}

                                    {renderValueField()}

                                    <button
                                        type="button"
                                        className="btn btn-transperant"
                                        onClick={handleAddDependency}
                                        disabled={!canAdd || depEntries.length >= MAX_DEPENDENCIES}
                                        title={
                                            !depKey
                                                ? t('Select a field first')
                                                : !isNonEmpty(depValue)
                                                    ? t('Enter a value')
                                                    : t('Add')
                                        }
                                    >
                                        <FontAwesomeIcon icon={faPlus} className="me-1" />
                                    </button>
                                </div>

                                <div>
                                    <div className="d-flex align-items-center justify-content-between mb-1">
                                        <div className="fw-semibold">
                                            {t(`Added Dependencies (${depEntries.length})`)}
                                        </div>

                                        {depEntries.length >= MAX_DEPENDENCIES && (
                                            <small className="text-muted ms-2">
                                                {t('Maximum 5 allowed')}
                                            </small>
                                        )}
                                    </div>
                                    <div className="added-deps-box"
                                    >
                                        {depEntries.length === 0 ? (
                                            <div className="text-muted small">
                                                {t('No dependencies added yet.')}
                                            </div>
                                        ) : (
                                            <ul className="mb-0 ps-0">
                                                {depEntries.map((d, idx) => (
                                                    <li
                                                        key={`${d.key}-${d.value}-${idx}`}
                                                        className="d-flex align-items-center gap-2"
                                                    >
                                                        <span className="flex-grow-1">
                                                            <code>{d.key} {d.operator} {d.value}</code>
                                                        </span>

                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-link text-danger p-0"
                                                            onClick={() => handleRemoveEntry(idx)}
                                                            title={t('Remove')}
                                                        >
                                                            <FontAwesomeIcon icon={faXmark} />
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>

                                {/* Step 1 footer buttons */}
                                <div className="d-flex justify-content-end gap-2 mt-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={handleCloseDialog}
                                    >
                                        {t('Cancel')}
                                    </button>

                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleNext}
                                        disabled={!canGoNext}
                                    >
                                        {t('Next')}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            /* STEP 2: summary */
                            <div className="d-flex flex-column gap-3">
                                <div>
                                    <div className="fw-semibold mb-1">{t('Dependency Group Name')}</div>
                                    <div>{newGroupName}</div>
                                </div>
                                <div>
                                    <div className="fw-semibold mb-1">{t('Filters')}</div>
                                    <pre
                                        className="manage-deps-json-preview added-deps-box"
                                    >
                                        {formatForConditionPreview(filtersWithOps)}
                                    </pre>
                                </div>

                                {/* Step 2 footer buttons */}
                                <div className="d-flex justify-content-end gap-2 mt-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        onClick={() => setDialogStep(1)}
                                    >
                                        {t('Back')}
                                    </button>

                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleCreate}
                                    >
                                        {t('Create')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    {createLoading && <LoadingOverlay />}
                </Dialog>

                <Dialog
                    open={deleteErrorOpen}
                    onClose={handleCloseDeleteError}
                    fullWidth
                    maxWidth="sm"
                >
                    <div className="p-3">
                        <h5 className="mb-3">{t('Cannot delete dependency group')}</h5>
                        <div className="mb-2">{deleteErrorInfo.message}</div>

                        <div className="mb-3">
                            <div className="fw-semibold">{t('Dependency Group')}</div>
                            <div>{deleteErrorInfo.groupName}</div>
                        </div>

                        <div className="mb-3">
                            <div className="fw-semibold mb-2">{t('Used by package versions')}</div>
                            {deleteErrorInfo.dependencies.length > 0 ? (
                                <ul className="mb-0 ps-3">
                                    {deleteErrorInfo.dependencies.map((item, idx) => (
                                        <li key={idx}>
                                            {item.packageName} : {item.versionName}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <div className="text-muted">{t('No package versions found.')}</div>
                            )}
                        </div>

                        <div className="d-flex justify-content-end">
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleCloseDeleteError}
                            >
                                {t('Close')}
                            </button>
                        </div>
                    </div>
                </Dialog>

                {loading && <LoadingOverlay />}
            </div>
        </div>
    );
}
